import { EmojiRenderer } from "@circle-react-shared/EmojiRenderer";
import { SuggestionMenu } from "@circle-react-shared/SuggestionMenu";
import type { SuggestionItem } from "@circle-react-shared/SuggestionMenu/types";
import type { SuggestionListHandle } from "@circle-react-shared/SuggestionMenu/useSuggestionListKeyboard";
import { useSuggestionListKeyboard } from "@circle-react-shared/SuggestionMenu/useSuggestionListKeyboard";
import { useInternalEditorState } from "@circle-react-shared/uikit/TipTap/internalEditorState";
import { useTipTapEditorContext } from "@circle-react-uikit/TipTap/TipTapEditorContext";
import { useBlockEditorContext } from "@circle-react-uikit/TipTapBlockEditor/BlockEditorContext";
import { reactQueryGet } from "@circle-react/helpers/backendRequestHelpers";
import { internalApi } from "@circle-react/helpers/urlHelpers";
import type { Editor } from "@tiptap/core";
import { groupBy, isArray } from "lodash";
import { forwardRef, useEffect, useMemo } from "react";
import { keepPreviousData, useQuery } from "react-query";

import { useThemeContext } from "@/react/providers/Theme";

import { getEntityTypeLabel } from "./helpers";

interface Entity {
  custom_emoji_dark_url?: string;
  custom_emoji_url?: string;
  emoji?: string;
  id: number | string;
  name: string;
  sgid: string;
  type: string;
}

export interface CompassEntityListProps {
  clientRect?: (() => DOMRect | null) | null;
  command: (item: Entity) => void;
  editor: Editor;
  query: string;
  searchType?: string;
}

export const CompassEntityList = forwardRef<
  SuggestionListHandle,
  CompassEntityListProps
>(({ clientRect, command, editor, query, searchType = "entity_list" }, ref) => {
  const tiptapEditorContext = useTipTapEditorContext();
  const tiptapBlockEditorContext = useBlockEditorContext();
  const { addToLocalSgidToObjectMap } =
    tiptapEditorContext || tiptapBlockEditorContext;
  const { setPopupsVisible } = useInternalEditorState();
  const { currentAppearance } = useThemeContext();

  const { data: items = [], isInitialLoading } = useQuery({
    queryKey: ["entities", searchType, query],
    queryFn: () =>
      reactQueryGet(
        internalApi.searchV2.advanced.index({
          params: {
            query,
            per_page: 10,
            page: 1,
            type: searchType,
          },
        }),
      ),
    select: ({ records }: { records?: Entity[] }) => records || [],
    enabled: true,
    retry: 1,
    placeholderData: keepPreviousData,
  });

  const entities: Entity[] = isArray(items) ? items : [];

  const { isOpen, menuRef } = useSuggestionListKeyboard({
    ref,
    query,
    isEnabled: entities.length > 0 && !isInitialLoading,
  });

  useEffect(() => {
    setPopupsVisible(isOpen);

    return () => setPopupsVisible(false);
  }, [isOpen, setPopupsVisible]);

  const groups = useMemo(
    () =>
      Object.entries(groupBy(entities, "type")).map(([type, members]) => ({
        id: type,
        label: getEntityTypeLabel(type),
        items: members.map(entity => ({
          id: String(entity.id),
          label: entity.name,
          visual:
            entity.type === "space" ? (
              <EmojiRenderer
                className="!text-dark flex !h-5 !w-auto items-center"
                emoji={entity.emoji}
                customEmojiUrl={entity.custom_emoji_url}
                customEmojiDarkUrl={entity.custom_emoji_dark_url}
                appearance={currentAppearance}
              />
            ) : undefined,
          data: entity,
        })),
      })),
    [entities, currentAppearance],
  );

  const onSelect = (item: SuggestionItem) => {
    const entity = item.data as Entity;
    addToLocalSgidToObjectMap({ sgid: entity.sgid, object: entity });
    command(entity);
    setPopupsVisible(false);
  };

  return (
    <SuggestionMenu
      ref={menuRef}
      open={isOpen}
      groups={groups}
      getAnchorRect={() => clientRect?.() ?? null}
      anchorContextElement={editor?.view.dom}
      onSelect={onSelect}
      side={tiptapEditorContext ? "top" : "bottom"}
    />
  );
});

CompassEntityList.displayName = "CompassEntityList";
